import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { api } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

const emptyStage = () => ({ label: '', target_date: '', completed_date: '' });
const toInput = (value) => (value ? String(value).slice(0, 10) : '');

/**
 * Admin definition of the project plan (main stages + dates).
 * This is exactly what investors see in the public "Project plan" section.
 */
export const ProjectPlanPanel = ({ projectId }) => {
  const [stages, setStages] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/v2/projects/${projectId}/plan`)
      .then((res) => {
        const loaded = (res.data.stages || []).map((s) => ({
          label: s.label || '',
          target_date: toInput(s.target_date),
          completed_date: toInput(s.completed_date),
        }));
        setSuggested(res.data.suggested_labels || []);
        setStages(loaded.length ? loaded : (res.data.suggested_labels || []).map((l) => ({ ...emptyStage(), label: l })));
      })
      .catch(() => setStages([emptyStage()]));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const setStage = (index, patch) => {
    setStages((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/v2/projects/${projectId}/plan`, {
        stages: stages
          .filter((s) => s.label.trim())
          .map((s) => ({
            label: s.label.trim(),
            target_date: s.target_date || null,
            completed_date: s.completed_date || null,
          })),
      });
      toast.success('Project plan saved - investors now see these stages');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-black/[0.06]" data-testid="project-plan-panel">
      <div className="px-5 py-3.5 border-b border-black/[0.05]">
        <h3 className="text-sm font-medium">Project plan</h3>
        <p className="text-xs text-foreground-muted mt-0.5">
          Main stages and dates shown to investors. A stage with a completion date is marked done; the first stage
          without one becomes the current stage.
        </p>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="hidden md:grid grid-cols-[1fr_150px_150px_36px] gap-3 text-[11px] uppercase tracking-[0.14em] text-foreground-muted">
          <span>Stage</span>
          <span>Target date</span>
          <span>Completed on</span>
          <span />
        </div>

        {stages.map((stage, i) => (
          <div key={i} className="grid md:grid-cols-[1fr_150px_150px_36px] gap-2 md:gap-3 items-center" data-testid={`plan-stage-row-${i}`}>
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-black/20 hidden md:block" />
              <Input
                value={stage.label}
                onChange={(e) => setStage(i, { label: e.target.value })}
                placeholder="e.g., Renovate"
                data-testid={`plan-stage-label-${i}`}
              />
            </div>
            <div>
              <Label className="md:hidden text-[11px]">Target date</Label>
              <Input
                type="date"
                value={stage.target_date}
                onChange={(e) => setStage(i, { target_date: e.target.value })}
                data-testid={`plan-stage-target-${i}`}
              />
            </div>
            <div>
              <Label className="md:hidden text-[11px]">Completed on</Label>
              <Input
                type="date"
                value={stage.completed_date}
                onChange={(e) => setStage(i, { completed_date: e.target.value })}
                data-testid={`plan-stage-completed-${i}`}
              />
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-foreground-muted hover:text-red-600 justify-self-start"
              onClick={() => setStages((prev) => prev.filter((_, idx) => idx !== i))}
              data-testid={`plan-stage-remove-${i}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStages((prev) => [...prev, emptyStage()])}
            data-testid="plan-add-stage"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add stage
          </Button>
          {suggested.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-foreground-muted"
              onClick={() => setStages(suggested.map((l) => ({ ...emptyStage(), label: l })))}
              data-testid="plan-reset-suggested"
            >
              Use default stages ({suggested.join(' → ')})
            </Button>
          )}
          <Button className="ml-auto" size="sm" onClick={save} disabled={saving} data-testid="plan-save-btn">
            {saving ? 'Saving…' : 'Save plan'}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProjectPlanPanel;
